import { registerBlock } from "../../registry";
import { makeId } from "../../helpers";
import type { ContentBlock } from "../../types";

const warfarin: ContentBlock<"medication"> = {
  id: makeId("med-warfarin"),
  kind: "medication",
  tags: ["medications", "her", "pre-cycle", "autoimmune"],
  title: "Warfarin",
  subtitle: "Vitamin K antagonist anticoagulant",
  body: {
    genericName: "Warfarin sodium",
    drugClass: "Vitamin K antagonist anticoagulant",
    purpose:
      "A blood thinner used for clot prevention in selected medical conditions. It is not a routine fertility medication, but it matters in fertility planning because many supplements, antibiotics, hormones, and procedures can change bleeding risk or INR control. Patients trying to conceive or pregnant generally need specialist anticoagulation planning because warfarin is usually avoided in pregnancy except in narrow high-risk situations.",
    commonRoutes: ["oral"],
    commonForms: ["pill"],
    dosageRanges:
      "Highly individualized based on INR monitoring. Never adjust dose based on a supplement or medication interaction without the anticoagulation prescriber.",
    howToTake:
      "Step 1: Keep INR monitoring - Take exactly as prescribed and keep INR checks on schedule. | Step 2: Keep vitamin K consistent - Sudden changes in vitamin K intake can change INR. | Step 3: Tell your fertility clinic - Report warfarin before egg retrieval, surgery, embryo transfer planning, pregnancy attempts, or starting supplements. | ⚠ When to call - Seek urgent care for heavy bleeding, black stools, vomiting blood, severe headache, trauma, or pregnancy.",
    sideEffects: [
      "Easy bruising",
      "Prolonged bleeding",
      "Nosebleeds or gum bleeding",
      "Heavy menstrual bleeding",
      "GI bleeding (serious)",
      "Medication and supplement interaction sensitivity",
    ],
    storage:
      "Store at room temperature away from moisture and heat. Keep out of reach of children.",
    aliases: ["Coumadin", "Jantoven", "Warfarine"],
    forWhom: "her",
    availability: [
      { name: "Coumadin", countries: ["US", "CA"] },
      { name: "Jantoven", countries: ["US"] },
      { name: "Warfarin", countries: ["US", "GB", "EU", "CA", "AU"] },
    ],
    frBody: {
      genericName: "Warfarine sodique",
      drugClass: "Anticoagulant antivitamine K",
      purpose:
        "Anticoagulant utilisé pour la prévention des caillots dans certaines pathologies. Ce n'est pas un médicament de fertilité de routine, mais il est important dans la planification de la fertilité car de nombreux compléments alimentaires, antibiotiques, hormones et procédures peuvent modifier le risque de saignement ou le contrôle de l'INR. Les patientes essayant de concevoir ou enceintes nécessitent généralement une planification spécialisée de l'anticoagulation, car la warfarine est habituellement évitée pendant la grossesse, sauf dans des situations à haut risque très ciblées.",
      dosageRanges:
        "Hautement individualisée selon la surveillance de l'INR. Ne modifiez jamais la dose en raison d'une interaction avec un complément alimentaire ou un médicament sans en parler au prescripteur de l'anticoagulation.",
      howToTake:
        "Maintenir la surveillance de l'INR - Prenez exactement comme prescrit et respectez le calendrier des contrôles d'INR. | Garder un apport en vitamine K stable - Des changements brusques d'apport en vitamine K peuvent modifier l'INR. | Informer votre centre de fertilité - Signalez la warfarine avant une ponction ovocytaire, une chirurgie, la planification d'un transfert d'embryon, des tentatives de grossesse ou l'introduction de compléments. | ⚠ Quand appeler - Consultez en urgence en cas de saignement abondant, selles noires, vomissements de sang, céphalée sévère, traumatisme ou grossesse.",
      sideEffects: [
        "Ecchymoses faciles",
        "Saignement prolongé",
        "Saignements de nez ou des gencives",
        "Règles abondantes",
        "Saignement digestif (grave)",
        "Sensibilité aux interactions médicamenteuses et avec les compléments alimentaires",
      ],
      storage:
        "Conservez à température ambiante, à l'abri de l'humidité et de la chaleur. Tenir hors de portée des enfants.",
    },
  },
  sources: [
    {
      title: "Warfarin sodium prescribing information",
      journal: "FDA labeling",
      year: 2024,
      url: "https://www.accessdata.fda.gov/scripts/cder/daf/",
      evidenceLevel: "expert-opinion",
    },
  ],
};

registerBlock(warfarin);
